import { formatAmount } from "@/D2C/features/auth/utils/utils";
import { App_Url_New } from "@/shared/common/static";
import { Checkbox } from "@/shared/components/Checkbox";
import InsurerLogoImage from "@/shared/components/InsurerLogoImage";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { addQuoteToCompare } from "@/shared/redux/reducers/commonSlice";
import {
  setQuoteFlowBindData,
  setQuoteFlowPaymentStatus,
  setQuoteFlowQuoteData,
} from "@/shared/redux/reducers/quoteFlowSlice";
import { openModal } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { isNotCovered } from "@/shared/utils/coverage";
import { extractInsurerLogoFile } from "@/shared/utils/getInsurarLogo";
import { getCheckedQuoteAddOnItems, getQuoteDisplayTotal } from "@/shared/utils/quoteAddOns";
import { Download } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useAppDispatch } from "../../redux/store/hooks";
import { Button } from "../Button";
import CurrencySymbol from "./CurrencySymbol";
import QuoteDetail from "./QuoteDetail";
import {
  isPositiveNumberLike,
  shouldShowQuoteItem,
  shouldUseAmountForDisplay,
} from "./quoteDisplay";

interface QuoteCardProps {
  isLoading?: boolean;
  data?: any;
  compact?: boolean;
  onBuyNow?: (quote: any) => void;
  LOB_ID?: number;
  disableBuyNow?: boolean;
  isReadOnly?: boolean;
  buyNowLoading?: boolean;
  showPdfButton?: boolean;
  onDownloadPdf?: (quote: any) => void;
}

const isBrandLogoNeedingTighterFit = (name?: string) =>
  /insurehub|nexinsure|nexaccel/i.test(String(name || ""));

const QuoteCard: React.FC<QuoteCardProps> = ({
  data,
  isLoading,
  compact = false,
  onBuyNow,
  LOB_ID,
  disableBuyNow = false,
  isReadOnly = false,
  buyNowLoading = false,
  showPdfButton = false,
  onDownloadPdf,
}) => {
  const [isBinding, setIsBinding] = useState(false);

  const {
    auth: { session, user },
    commonSlice: { quoteList },
  } = usePosterReducers();

  const dispatch = useAppDispatch();
  const savedClientId = session?.client?.id;
  const savedSessionId = session?.session_data?.id;
  const showCommission = Boolean(user?.access_token);

  console.log("showCommission:", showCommission);
  console.log("commission:", data?.plan?.commission);
  console.log("commission condition:", Boolean(showCommission && data?.commission));

  const compareQuote = quoteList.filter((quote) => quote.isCompare);
  const isCompared = compareQuote.some(
    (quote) =>
      `${quote?.insurer?.name}_${quote?.plan?.name}_${quote?.plan?.price?.amount}` ===
      `${data?.insurer?.name}_${data?.plan?.name}_${data?.plan?.price?.amount}`
  );
  const displayPrice = getQuoteDisplayTotal(data);

  const handleBuyNow = async () => {
    if (disableBuyNow || isReadOnly) {
      return;
    }

    if (onBuyNow) {
      onBuyNow(data);
      return;
    }

    setIsBinding(true);

    try {
      const insurerPrefix = data?.insurer?.name
        ?.replace(/insurance|insurer|pjsc|limited|co/gi, "")
        .trim();

      const payload = {
        client_id: savedClientId ? parseInt(savedClientId as string, 10) : null,
        lob_id: LOB_ID,
        session_id: savedSessionId ? parseInt(savedSessionId as string, 10) : null,
        plan_name: insurerPrefix ? `${insurerPrefix}-${data?.plan?.name}` : data?.plan?.name,
        insurerId: String(data?.insurer?.insurer_id ?? ""),
        action: "BUY",
        insurerPlan: {
          id: String(data?.plan?.id ?? ""),
          planName: data?.plan?.name || "",
        },
        selectedOptionalCovers: [],
        additionalPersonalDetails: {
          gender: "",
          beneficiaryName: "",
          beneficiaryRelationship: "",
          address: "",
          nationality: "",
          sourceOfFunds: "",
          cityOfResidence: "",
        },
        identity: {
          emiratesId: "",
          emiratesIdExpiryDate: "",
          emiratesIdIssueDate: "",
          passportNo: "",
          passportExpiryDate: "",
          passportIssueDate: "",
        },
        declarations: {
          isUaeResidentConfirmed: false,
        },
        attachments: [],
      };

      const response = await axiosInstance.post(App_Url_New.ApiEndPoint.bindQuote, payload);

      if (response.status === 200 || response.data) {
        dispatch(setQuoteFlowQuoteData(data));
        dispatch(setQuoteFlowBindData(response.data));
        dispatch(setQuoteFlowPaymentStatus("IDLE"));
        // navigate("/travel-get-quote?step=4", {
        //   state: {
        //     quoteData: data,
        //     bindData: response.data,
        //     reset: true,
        //   },
        // });
      }
    } finally {
      setIsBinding(false);
    }
  };

  const callOpenQuoteDetails = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.QUOTE_DETAILS,
        className: "!w-full !h-[65vh] !max-h-[65vh] overflow-y-auto py-3 thin-scrollbar",
        outerClassName: "items-end",
        title: "",
        children: <QuoteDetail data={data} onBuyNow={onBuyNow} isReadOnly={isReadOnly} />,
      })
    );
  };

  const formatCoverValue = (item: any) => {
    if (!item) {
      return <span className="text-lg font-semibold">✕</span>;
    }

    if (item?.label === "Basic Cover") {
      return (
        <span className="text-base font-semibold text-theme-success-text-strong">Included</span>
      );
    }

    if (shouldUseAmountForDisplay(item)) {
      return (
        <div className="flex items-center text-[13px] font-semibold">
          <CurrencySymbol currency={item.currency} value={item.amount} />
          <p>{item.amount ?? "-"}</p>
        </div>
      );
    }

    if (isPositiveNumberLike(item?.amount)) {
      return (
        <div className="flex items-center text-[13px] font-semibold">
          <CurrencySymbol currency={item.currency} value={item.amount} />
          <p>{item.amount ?? "-"}</p>
        </div>
      );
    }

    if (item?.value === true) {
      return (
        <span className="text-base font-semibold text-theme-success-text-strong">Included</span>
      );
    }

    if (isNotCovered(item.value)) {
      return <span className="text-lg font-semibold">✕</span>;
    }

    return (
      <div className="flex items-center text-[13px] font-semibold">
        <CurrencySymbol currency={item.currency} value={item.value} />
        <p>{item.value ?? "-"}</p>
      </div>
    );
  };

  const getCardSections = (sections: any[] = []) => {
    const normalizedSections = sections.filter((section: any) => section?.title !== "Add-Ons");
    const preferredTitles = new Set(["section 1", "section 2"]);
    const preferredSections = normalizedSections.filter((section: any) =>
      preferredTitles.has(
        String(section?.title || "")
          .trim()
          .toLowerCase()
      )
    );

    return preferredSections.length > 0 ? preferredSections : normalizedSections;
  };

  const { primaryItems, secondaryItems, selectedAddOnItems, usesPreferredSections } =
    useMemo(() => {
      const normalizedSections = (data?.sections || []).filter(
        (section: any) => section?.title !== "Add-Ons"
      );
      const baseSections = getCardSections(data?.sections || []);
      const usesPreferredSections = normalizedSections.some((section: any) =>
        ["section 1", "section 2"].includes(
          String(section?.title || "")
            .trim()
            .toLowerCase()
        )
      );
      const getVisibleItems = (section: any) =>
        (section?.items || [])
          .filter(
            (item: any) =>
              item?.label !== "Excess Amount" &&
              item?.label !== "Commission %" &&
              item?.label !== "Commission Amount" &&
              shouldShowQuoteItem(item)
          )
          .map((item: any) => {
            if (item?.label === "Breakdown Recovery") {
              return {
                ...item,
                label: "24 Hours Accident Recovery",
              };
            }

            if (item?.label === "Premium Rent A Car") {
              return {
                ...item,
                label: "Premium Rent A Car",
              };
            }

            return item;
          });

      const visibleItems = baseSections.flatMap(getVisibleItems);
      const preferredSection1 = baseSections.find(
        (section: any) =>
          String(section?.title || "")
            .trim()
            .toLowerCase() === "section 1"
      );
      const preferredSection2 = baseSections.find(
        (section: any) =>
          String(section?.title || "")
            .trim()
            .toLowerCase() === "section 2"
      );

      return {
        primaryItems: usesPreferredSections
          ? getVisibleItems(preferredSection1)
          : visibleItems.slice(0, compact ? 2 : 3),
        secondaryItems: usesPreferredSections
          ? getVisibleItems(preferredSection2)
          : visibleItems.slice(compact ? 2 : 3, compact ? 6 : 9),
        selectedAddOnItems: getCheckedQuoteAddOnItems(data),
        usesPreferredSections,
      };
    }, [compact, data?.sections]);

  if (isLoading) {
    return (
      <div
        className={`animate-pulse rounded-lg border border-theme-border bg-theme-surface ${
          compact ? "m-0 min-h-[180px] p-3" : "m-2 min-h-[260px] p-6"
        }`}
      />
    );
  }
  return (
    <div
      className={`flex flex-col overflow-hidden rounded-lg border border-theme-border bg-theme-surface md:flex-row ${
        compact ? "m-0" : "m-2"
      }`}
    >
      {!compact ? (
        <div className="flex w-full flex-col items-center p-4 md:w-60">
          <div className="mb-3 flex h-20 w-44 items-center justify-center">
            <InsurerLogoImage
              insurerName={data?.insurer?.name}
              insurerLogoFile={extractInsurerLogoFile(data?.insurer)}
              alt="insurer"
              className={`object-contain ${
                isBrandLogoNeedingTighterFit(data?.insurer?.name)
                  ? "max-h-14 max-w-36"
                  : "max-h-16 max-w-40"
              }`}
            />
          </div>

          <div className="mt-auto w-full text-center">
            <div className="mb-1 inline-block rounded-full bg-theme-surface-muted px-3 py-1 text-xs font-semibold">
              {data?.plan?.name}
            </div>

            <p className="flex items-center justify-center gap-1 whitespace-nowrap text-xl font-bold">
              <CurrencySymbol currency={data?.plan?.price?.currency} value={displayPrice} />
              {formatAmount(displayPrice)}
            </p>
          </div>
        </div>
      ) : null}

      <div className={`flex-1 border-l border-theme-border ${compact ? "p-3" : "p-4 sm:p-5"}`}>
        {compact ? (
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-xs font-semibold">{data?.insurer?.name}</h3>
              <span className="inline-block rounded-full bg-theme-surface-muted px-2 py-0.5 text-[10px] text-theme-text-soft">
                {data?.plan?.name}
              </span>
            </div>
            <div className="flex flex-col items-end">
              <p className="flex shrink-0 items-center gap-0.5 whitespace-nowrap text-sm font-bold">
                <CurrencySymbol currency={data?.plan?.price?.currency} value={displayPrice} />
                {formatAmount(displayPrice)}
              </p>

              {showCommission && data?.plan?.commission && (
                <p className="text-right text-[13px]">
                  Commission:{" "}
                  <span className="font-semibold">
                    {data.plan.commission.percentage ?? 0}% (
                    {data.plan.commission.currency ?? LOCALISATION_CONFIG.currencyCode}{" "}
                    {formatAmount(data.plan.commission.amount ?? 0)})
                  </span>
                </p>
              )}
            </div>
          </div>
        ) : (
          <h3 className="mb-4 text-lg font-semibold">{data?.insurer?.name}</h3>
        )}

        <div className={compact ? "space-y-2" : "space-y-4"}>
          <div
            className={
              compact
                ? "flex flex-row flex-wrap items-start gap-x-4 gap-y-1"
                : "grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-3"
            }
          >
            {primaryItems?.map((item: any, index: number) => (
              <div
                key={`${item?.label || "primary"}-${item?.value || item?.amount || index}-${index}`}
                className={compact ? "whitespace-nowrap" : ""}
              >
                <p className="mb-0.5 text-[12px] text-theme-text-soft">{item?.label}:</p>
                {formatCoverValue(item)}
              </div>
            ))}
          </div>

          {secondaryItems.length > 0 || selectedAddOnItems.length > 0 ? (
            <>
              <hr className="my-2 -mx-4 border-theme-border sm:-mx-5" />

              {secondaryItems.length > 0 ? (
                <div>
                  {!usesPreferredSections ? (
                    <h4
                      className={`font-semibold text-theme-primary ${compact ? "mb-1 text-xs" : "mb-3"}`}
                    >
                      Additional covers
                    </h4>
                  ) : null}
                  <div
                    className={
                      compact
                        ? "flex flex-row flex-wrap gap-x-4 gap-y-1"
                        : "grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-3"
                    }
                  >
                    {secondaryItems.map((item: any, index: number) => (
                      <div
                        key={`${item?.label || "secondary"}-${item?.value || item?.amount || index}-${index}`}
                        className={compact ? "whitespace-nowrap" : ""}
                      >
                        <p className="mb-0.5 text-[12px] text-theme-text-soft">{item?.label}:</p>
                        {formatCoverValue(item)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedAddOnItems?.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {selectedAddOnItems?.map((item: any, index: number) => (
                    <div
                      key={`${item?.label || "addon"}-${item?.amount || item?.value || index}-${index}`}
                      className="flex items-center gap-2 rounded-full bg-theme-surface-soft px-3 py-2"
                    >
                      <span className="flex h-4 w-4 items-center justify-center rounded border border-theme-success-text-strong bg-theme-success text-xs font-bold text-white">
                        ✓
                      </span>
                      <span className="text-[12px] font-medium text-theme-text-muted">
                        {item?.label}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div
          className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${
            compact ? "mt-2" : "mt-4"
          }`}
        >
          <div className="flex flex-wrap gap-2">
            <button
              className="text-[11px] font-semibold text-theme-primary"
              onClick={callOpenQuoteDetails}
            >
              View Details
            </button>

            {!compact ? (
              <>
                <button className="text-[11px] font-semibold text-theme-primary">
                  Table of Benefits
                </button>
                <button className="text-[11px] font-semibold text-theme-primary">
                  Terms & Conditions
                </button>
              </>
            ) : null}
            <Checkbox
              color="green"
              label="Add To Compare"
              labelClassName="text-theme-text-muted"
              checked={isCompared}
              className="text-[11px]"
              wrapperClassName="text-[11px]"
              onChange={() => {
                dispatch(addQuoteToCompare(data));
              }}
            />
            {showPdfButton ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-theme-text"
                onClick={() => onDownloadPdf?.(data)}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download PDF
              </button>
            ) : null}
          </div>
          <Button
            onClick={handleBuyNow}
            isLoading={isBinding || buyNowLoading}
            disabled={disableBuyNow || isReadOnly || buyNowLoading}
            loadingText="Processing..."
            className={
              compact ? "min-w-[88px] px-4 py-1.5 text-xs" : "min-w-[108px] px-6 py-2 text-sm"
            }
          >
            Buy Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;
